import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CogIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  ComputerDesktopIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const YesPartialNoRadio = ({ name, value, onChange, className = '' }) => {
  const options = [
    { value: 'yes', label: 'Yes', color: 'text-green-600' },
    { value: 'partial', label: 'Partial', color: 'text-yellow-600' },
    { value: 'no', label: 'No', color: 'text-red-600' },
  ];

  return (
    <div className={`flex gap-4 ${className}`}>
      {options.map(option => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={e => onChange(e.target.value)}
            className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-500"
          />
          <span className={`text-sm font-medium ${option.color}`}>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const SupportEcosystemAssessment = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    accessToFinance: {
      fundingPathways: data.accessToFinance?.fundingPathways || 'no',
      fundingReady: data.accessToFinance?.fundingReady || 'no',
      pitchDecks: data.accessToFinance?.pitchDecks || 'no',
      investorBriefs: data.accessToFinance?.investorBriefs || 'no',
      notes: data.accessToFinance?.notes || '',
    },
    accessToMarkets: {
      pilotTesting: data.accessToMarkets?.pilotTesting || 'no',
      partnerships: data.accessToMarkets?.partnerships || 'no',
      distributionChannels: data.accessToMarkets?.distributionChannels || 'no',
      demoDays: data.accessToMarkets?.demoDays || 'no',
      tradeShows: data.accessToMarkets?.tradeShows || 'no',
      corporateSupplyChains: data.accessToMarkets?.corporateSupplyChains || 'no',
      notes: data.accessToMarkets?.notes || '',
    },
    platformIntegration: {
      digitalPlatform: data.platformIntegration?.digitalPlatform || 'no',
      clientLifecycle: data.platformIntegration?.clientLifecycle || 'no',
      digitizedInterventions: data.platformIntegration?.digitizedInterventions || 'no',
      digitizedAssessments: data.platformIntegration?.digitizedAssessments || 'no',
      digitizedDocumentation: data.platformIntegration?.digitizedDocumentation || 'no',
      notes: data.platformIntegration?.notes || '',
    },
    alumniSupport: {
      maintainRelationships: data.alumniSupport?.maintainRelationships || 'no',
      postExitImpact: data.alumniSupport?.postExitImpact || 'no',
      alumniMentors: data.alumniSupport?.alumniMentors || 'no',
      trackingSystem: data.alumniSupport?.trackingSystem || 'no',
      notes: data.alumniSupport?.notes || '',
    },
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const getPoints = value => {
      if (value === 'yes') return 1;
      if (value === 'partial') return 0.5;
      return 0;
    };

    const accessToFinance = assessmentData.accessToFinance;
    totalPoints += getPoints(accessToFinance.fundingPathways);
    totalPoints += getPoints(accessToFinance.fundingReady);
    totalPoints += getPoints(accessToFinance.pitchDecks);
    totalPoints += getPoints(accessToFinance.investorBriefs);
    maxPoints += 4;

    const accessToMarkets = assessmentData.accessToMarkets;
    totalPoints += getPoints(accessToMarkets.pilotTesting);
    totalPoints += getPoints(accessToMarkets.partnerships);
    totalPoints += getPoints(accessToMarkets.distributionChannels);
    totalPoints += getPoints(accessToMarkets.demoDays);
    totalPoints += getPoints(accessToMarkets.tradeShows);
    totalPoints += getPoints(accessToMarkets.corporateSupplyChains);
    maxPoints += 6;

    const platformIntegration = assessmentData.platformIntegration;
    totalPoints += getPoints(platformIntegration.digitalPlatform);
    totalPoints += getPoints(platformIntegration.clientLifecycle);
    totalPoints += getPoints(platformIntegration.digitizedInterventions);
    totalPoints += getPoints(platformIntegration.digitizedAssessments);
    totalPoints += getPoints(platformIntegration.digitizedDocumentation);
    maxPoints += 5;

    const alumniSupport = assessmentData.alumniSupport;
    totalPoints += getPoints(alumniSupport.maintainRelationships);
    totalPoints += getPoints(alumniSupport.postExitImpact);
    totalPoints += getPoints(alumniSupport.alumniMentors);
    totalPoints += getPoints(alumniSupport.trackingSystem);
    maxPoints += 4;

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const getScoreColor = score => {
    if (score >= 81) return 'text-green-600';
    if (score >= 61) return 'text-yellow-600';
    if (score >= 41) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 81) return 'Healthy & Scalable';
    if (score >= 61) return 'Emerging Maturity';
    if (score >= 41) return 'At Risk';
    return 'Critical Gaps';
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <CogIcon className="h-8 w-8 text-indigo-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Support Ecosystem & Operational Integration
            </h2>
            <p className="text-gray-600">
              Assess the effectiveness of support systems, ecosystem connections, and operational
              integration
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-6 py-3 font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
          <h3 className="text-xl font-semibold text-gray-900">Access to Finance</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program link clients to funding pathways (investors, grants, banking)?
            </label>
            <p className="text-xs text-gray-600">
              Active facilitation of connections to various funding sources and financial
              institutions
            </p>
            <YesPartialNoRadio
              name="fundingPathways"
              value={assessmentData.accessToFinance.fundingPathways}
              onChange={value => updateField('accessToFinance', 'fundingPathways', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneurs supported in preparing to be funding-ready?
            </label>
            <p className="text-xs text-gray-600">
              Training and support to help entrepreneurs meet funding requirements and standards
            </p>
            <YesPartialNoRadio
              name="fundingReady"
              value={assessmentData.accessToFinance.fundingReady}
              onChange={value => updateField('accessToFinance', 'fundingReady', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneurs supported in creating pitch decks?
            </label>
            <p className="text-xs text-gray-600">
              Assistance with developing compelling presentation materials for investors
            </p>
            <YesPartialNoRadio
              name="pitchDecks"
              value={assessmentData.accessToFinance.pitchDecks}
              onChange={value => updateField('accessToFinance', 'pitchDecks', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneurs supported in preparing investor briefs?
            </label>
            <p className="text-xs text-gray-600">
              Help with creating comprehensive investment documentation and business summaries
            </p>
            <YesPartialNoRadio
              name="investorBriefs"
              value={assessmentData.accessToFinance.investorBriefs}
              onChange={value => updateField('accessToFinance', 'investorBriefs', value)}
            />
          </div>

          <div>
            <label
              htmlFor="accessToFinanceNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="accessToFinanceNotes"
              value={assessmentData.accessToFinance.notes}
              onChange={e => updateField('accessToFinance', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <BuildingStorefrontIcon className="h-6 w-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-gray-900">Access to Markets & Partners</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneurs assisted in pilot testing?
            </label>
            <p className="text-xs text-gray-600">
              Support for testing products or services with early customers and market validation
            </p>
            <YesPartialNoRadio
              name="pilotTesting"
              value={assessmentData.accessToMarkets.pilotTesting}
              onChange={value => updateField('accessToMarkets', 'pilotTesting', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneurs assisted in forming partnerships?
            </label>
            <p className="text-xs text-gray-600">
              Facilitation of strategic partnerships with other businesses, organizations, or
              institutions
            </p>
            <YesPartialNoRadio
              name="partnerships"
              value={assessmentData.accessToMarkets.partnerships}
              onChange={value => updateField('accessToMarkets', 'partnerships', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneurs assisted in accessing distribution channels?
            </label>
            <p className="text-xs text-gray-600">
              Support for connecting with distribution networks and sales channels
            </p>
            <YesPartialNoRadio
              name="distributionChannels"
              value={assessmentData.accessToMarkets.distributionChannels}
              onChange={value => updateField('accessToMarkets', 'distributionChannels', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are demo days part of the program?
            </label>
            <p className="text-xs text-gray-600">
              Organized events where entrepreneurs can showcase their products or services to
              potential customers and investors
            </p>
            <YesPartialNoRadio
              name="demoDays"
              value={assessmentData.accessToMarkets.demoDays}
              onChange={value => updateField('accessToMarkets', 'demoDays', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneurs assisted in participating in trade shows?
            </label>
            <p className="text-xs text-gray-600">
              Support for attending and participating in industry trade shows and exhibitions
            </p>
            <YesPartialNoRadio
              name="tradeShows"
              value={assessmentData.accessToMarkets.tradeShows}
              onChange={value => updateField('accessToMarkets', 'tradeShows', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are entrepreneurs linked with corporate supply chains?
            </label>
            <p className="text-xs text-gray-600">
              Connections with large corporations for supply chain opportunities and B2B
              partnerships
            </p>
            <YesPartialNoRadio
              name="corporateSupplyChains"
              value={assessmentData.accessToMarkets.corporateSupplyChains}
              onChange={value => updateField('accessToMarkets', 'corporateSupplyChains', value)}
            />
          </div>

          <div>
            <label
              htmlFor="accessToMarketsNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="accessToMarketsNotes"
              value={assessmentData.accessToMarkets.notes}
              onChange={e => updateField('accessToMarkets', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <ComputerDesktopIcon className="h-6 w-6 text-purple-600" />
          <h3 className="text-xl font-semibold text-gray-900">Platform & CRM Integration</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is a digital platform used to manage the client lifecycle?
            </label>
            <p className="text-xs text-gray-600">
              Use of comprehensive digital systems for managing all aspects of client engagement
            </p>
            <YesPartialNoRadio
              name="digitalPlatform"
              value={assessmentData.platformIntegration.digitalPlatform}
              onChange={value => updateField('platformIntegration', 'digitalPlatform', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is the client lifecycle fully digitized and tracked?
            </label>
            <p className="text-xs text-gray-600">
              Complete digital tracking from initial contact through program completion and beyond
            </p>
            <YesPartialNoRadio
              name="clientLifecycle"
              value={assessmentData.platformIntegration.clientLifecycle}
              onChange={value => updateField('platformIntegration', 'clientLifecycle', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are interventions digitized and documented?
            </label>
            <p className="text-xs text-gray-600">
              All program interventions, activities, and support services are digitally recorded
            </p>
            <YesPartialNoRadio
              name="digitizedInterventions"
              value={assessmentData.platformIntegration.digitizedInterventions}
              onChange={value =>
                updateField('platformIntegration', 'digitizedInterventions', value)
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are assessments digitized and tracked?
            </label>
            <p className="text-xs text-gray-600">
              All evaluation and assessment processes are conducted and recorded digitally
            </p>
            <YesPartialNoRadio
              name="digitizedAssessments"
              value={assessmentData.platformIntegration.digitizedAssessments}
              onChange={value => updateField('platformIntegration', 'digitizedAssessments', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is documentation digitized and accessible?
            </label>
            <p className="text-xs text-gray-600">
              All program documentation, reports, and records are stored and accessible digitally
            </p>
            <YesPartialNoRadio
              name="digitizedDocumentation"
              value={assessmentData.platformIntegration.digitizedDocumentation}
              onChange={value =>
                updateField('platformIntegration', 'digitizedDocumentation', value)
              }
            />
          </div>

          <div>
            <label
              htmlFor="platformIntegrationNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="platformIntegrationNotes"
              value={assessmentData.platformIntegration.notes}
              onChange={e => updateField('platformIntegration', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <UserGroupIcon className="h-6 w-6 text-orange-600" />
          <h3 className="text-xl font-semibold text-gray-900">Alumni Support</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the program maintain relationships with past clients?
            </label>
            <p className="text-xs text-gray-600">
              Ongoing engagement and relationship management with program alumni
            </p>
            <YesPartialNoRadio
              name="maintainRelationships"
              value={assessmentData.alumniSupport.maintainRelationships}
              onChange={value => updateField('alumniSupport', 'maintainRelationships', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there a system for tracking post-exit impact?
            </label>
            <p className="text-xs text-gray-600">
              Monitoring and measuring the long-term success and impact of program graduates
            </p>
            <YesPartialNoRadio
              name="postExitImpact"
              value={assessmentData.alumniSupport.postExitImpact}
              onChange={value => updateField('alumniSupport', 'postExitImpact', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there a system for inviting alumni back as mentors?
            </label>
            <p className="text-xs text-gray-600">
              Structured approach to engaging successful alumni as mentors for current participants
            </p>
            <YesPartialNoRadio
              name="alumniMentors"
              value={assessmentData.alumniSupport.alumniMentors}
              onChange={value => updateField('alumniSupport', 'alumniMentors', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there a comprehensive alumni tracking system?
            </label>
            <p className="text-xs text-gray-600">
              Systematic approach to maintaining alumni database and engagement tracking
            </p>
            <YesPartialNoRadio
              name="trackingSystem"
              value={assessmentData.alumniSupport.trackingSystem}
              onChange={value => updateField('alumniSupport', 'trackingSystem', value)}
            />
          </div>

          <div>
            <label
              htmlFor="alumniSupportNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="alumniSupportNotes"
              value={assessmentData.alumniSupport.notes}
              onChange={e => updateField('alumniSupport', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Overall Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
          placeholder="Add any overall observations, recommendations, or strategic insights..."
        />
      </motion.div>
    </div>
  );
};

export default SupportEcosystemAssessment;
